import { useState } from 'react'
import { Loader2 } from 'lucide-react'
import { Panel } from '@/components/erp/primitives'
import { ErrorStrip } from '@/shared/ui/states'
import { useCan } from '@/shared/auth/session'
import { explain } from '@/shared/api/problem'
import { fmtDateTime } from '@/shared/lib/format'
import type { TransferNoteResponse } from '@/shared/api/types'
import { addTransferNote } from './api'

/**
 * Caseworker notes, kept separate from decisions on purpose.
 *
 * A decision is a state transition with legal weight; a note is someone saying
 * "called her twice, no answer". Mixing them would make the decision log
 * unreadable as evidence, which is the one job it has.
 */
export function TransferNotes({
  requestId, notes, onChanged,
}: {
  requestId: string
  notes: TransferNoteResponse[]
  onChanged: () => void
}) {
  const canWrite = useCan('relocation:review')
  const [body, setBody] = useState('')
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)

  return (
    <Panel title="Notes" description="Working notes. Not part of the decision record.">
      <ErrorStrip message={error} />

      {notes.length === 0 && (
        <p className="text-erp-sm text-muted-foreground">No notes yet.</p>
      )}

      <ol className="space-y-2.5">
        {notes.map((n) => (
          <li key={n.id} className="text-erp-sm">
            <p className="whitespace-pre-wrap">{n.body}</p>
            <div className="text-erp-xs text-muted-foreground mt-0.5">
              {fmtDateTime(n.createdAt)}
              {n.internal ? ' · internal' : ' · shareable'}
            </div>
          </li>
        ))}
      </ol>

      {canWrite && (
        <div className="mt-3 pt-3 border-t border-border">
          <textarea
            className="erp-input w-full min-h-[60px]"
            value={body}
            onChange={(e) => setBody(e.target.value)}
            placeholder="What happened, for whoever picks this up next."
            aria-label="Add a note"
          />
          <div className="flex justify-end mt-2">
            <button
              type="button"
              className="erp-btn"
              disabled={!body.trim() || saving}
              onClick={async () => {
                setSaving(true); setError(null)
                try {
                  await addTransferNote(requestId, body.trim())
                  setBody('')
                  onChanged()
                } catch (e) { setError(explain(e)) } finally { setSaving(false) }
              }}
            >
              {saving && <Loader2 className="h-3.5 w-3.5 animate-spin" />} Add note
            </button>
          </div>
        </div>
      )}
    </Panel>
  )
}
